﻿import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert,
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Link,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';

import { SCUIButton, SCUILoadingLinear, SCUIZoomableImage } from '../../../atomic/atoms';
import { SCUIBreadCrumb, SCUISelectBox } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import { DeleteS3Object, UploadS3File } from '../../../services/command/aiPrompt/S3BrowserCommand.services';
import { GetS3PresignedDownloadUrls } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import { RawImageUploadQuery, type PhotoTypeItem } from '../../../services/studio/StudioQuery/RawImageUpload/RawImageUploadQuery.services';
import { RawImageUploadCommand } from '../../../services/studio/StudioCommand/RawImageUpload/RawImageUploadCommand.services';
import {
    BatchProcessTrackingQuery,
    type BarcodeProductionSummary,
} from '../../../services/studio/StudioQuery/BatchProcessTracking/BatchProcessTrackingQuery.services';
import { WorkAssignmentQuery } from '../../../services/studio/StudioQuery/WorkAssignment/WorkAssignmentQuery.services';
import { DEFAULT_MAX_DOWNLOAD_COUNT } from '../../../services/studio/StudioQuery/RawImageReview/RawImageReviewQuery.services';
import { ensureRetouchS3Folder } from '../Retouch/retouch.helpers';
import {
    STUDIO_WAREHOUSE_CODE,
    buildRawImagesUploadPrefix,
    buildStudioS3ObjectKey,
} from '../../utils/studioShotS3Path';
import { runWithConcurrencyLimit } from '../../../utils/concurrency';
import { BulkImageDropArea } from './BulkImageDropArea';
import { parseProductFileName } from './bulkImageUpload.helpers';
import { PageUrl } from '../../../types/enums/pageUrl-enums';
import { ApiURL } from '../../../types/constants/apiUrls/api-urls';

const RAW_SHOT_STARTED = 'RawShotStarted';
const WORK_ASSIGNMENT_FEATURE_KEY = 'rawImageReview';
const HISTORY_CHECK_DEBOUNCE_MS = 300;
const HISTORY_CHECK_CHUNK_SIZE = 100;

const getCurrentUsername = (): string | null => {
    try {
        const username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username;
        if (!username || typeof username !== 'string' || !username.trim()) {
            return null;
        }
        const trimmed = username.trim();
        if (trimmed.toLowerCase() === 'system') {
            return null;
        }
        return trimmed;
    } catch {
        return null;
    }
};

/** StudioAuditUserName.Normalize ile aynı (CreatedBy eşlemesi). */
const normalizeCreatedBy = (username: string) => username.trim().toLocaleLowerCase('tr-TR');

const fileNameFromPath = (filePath: string) => {
    const cleaned = (filePath || '').split('?')[0];
    const parts = cleaned.split('/');
    return parts[parts.length - 1] || cleaned || 'image';
};

type UploadedFileItem = {
    id: string;
    fileName: string;
    barcode: string;
    filePath?: string;
    photoTypeCode: string;
    photoTypeDisplayName: string;
    previewUrl: string;
    rawImageId?: number;
    uploadProgress: number;
    status: 'pending' | 'uploading' | 'registering' | 'done' | 'error' | 'invalid_format';
    errorMessage?: string;
};

type QueuedUploadItem = {
    uploadId: string;
    file: File;
    photoType: PhotoTypeItem;
    barcode: string;
    s3ObjectKey: string;
};

const createUploadId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
const BULK_UPLOAD_PATH = PageUrl.studioRoutes.bulkImageUpload.url;

const purgeRegisteredUploads = async (items: UploadedFileItem[]) => {
    const registered = items.filter((item) => item.rawImageId && item.barcode);
    const orphanS3 = items.filter((item) => !item.rawImageId && item.filePath);

    const deleteResults = await Promise.allSettled(
        registered.map((item) =>
            RawImageUploadCommand.DeleteFile({
                rawImageId: item.rawImageId!,
                barcode: item.barcode,
            }),
        ),
    );

    await Promise.allSettled(
        orphanS3.map((item) => DeleteS3Object({ key: item.filePath! })),
    );

    return {
        registeredCount: registered.length,
        failedCount: deleteResults.filter((result) => {
            if (result.status === 'rejected') {
                return true;
            }
            return !result.value.success;
        }).length,
    };
};

/** Sekme/kapatma sırasında axios iptal olabilir; keepalive fetch dener. */
const purgeRegisteredUploadsKeepalive = (items: UploadedFileItem[]) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
        return;
    }

    const url = ApiURL.Command.StudioCommandURLs.RawImageUploadCommandURL.DeleteFile;
    const selectedCountry = localStorage.getItem('selectedCountry');
    const parsedCountry = selectedCountry ? JSON.parse(selectedCountry) : {};
    const username = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username || '';
    const apiContext =
        '{storeCode:"' +
        (localStorage.getItem('storeCode') || '') +
        '",isStoreEmployee:' +
        (localStorage.getItem('isStoreEmployee') === 'true' ? 'true' : 'false') +
        ',operatorId:' +
        (JSON.parse(localStorage.getItem('checkOperator') || '{}')?.operatorId || '') +
        ',environment:"' +
        (import.meta.env.VITE_ENVIRONMENT || '') +
        '",regionid:' +
        (parsedCountry?.regionId || 0) +
        ',applicationid:1,culture:"' +
        (localStorage.getItem('lan') || '') +
        '",countryid:' +
        (parsedCountry?.countryId || 0) +
        ',countryCode:"' +
        (parsedCountry?.countryCode || 'TR') +
        '",username:"' +
        username +
        '"}';

    items
        .filter((item) => item.rawImageId && item.barcode)
        .forEach((item) => {
            void fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    'x-api-context': apiContext,
                },
                body: JSON.stringify({
                    rawImageId: item.rawImageId,
                    barcode: item.barcode,
                }),
                keepalive: true,
            });
        });
};

export const BulkImageUpload = () => {
    const { t } = useTranslation();
    const history = useHistory();

    const [photoTypes, setPhotoTypes] = useState<PhotoTypeItem[]>([]);
    const [photoTypesLoading, setPhotoTypesLoading] = useState(true);
    const [activePhotoTypeCode, setActivePhotoTypeCode] = useState('');
    const [files, setFiles] = useState<UploadedFileItem[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [isHydrating, setIsHydrating] = useState(false);
    const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

    const [previousProductions, setPreviousProductions] = useState<Record<string, BarcodeProductionSummary>>({});
    const [maxDownloadCount, setMaxDownloadCount] = useState(DEFAULT_MAX_DOWNLOAD_COUNT);
    const checkedHistoryBarcodesRef = useRef<Set<string>>(new Set());

    const filesRef = useRef(files);
    filesRef.current = files;
    const skipLeaveCleanupRef = useRef(false);

    const activePhotoType = useMemo(
        () => photoTypes.find((item) => item.code === activePhotoTypeCode) ?? null,
        [photoTypes, activePhotoTypeCode],
    );

    const uploadBasePrefix = useMemo(() => buildRawImagesUploadPrefix(), []);

    const photoTypeSelectList = useMemo(
        () =>
            photoTypes.map((item) => ({
                id: item.code,
                value: item.displayName || t(`studio.bulkImageUpload.tabs.${item.code}`, item.code),
            })),
        [photoTypes, t],
    );

    const fileCountByPhotoType = useMemo(() => {
        const counts: Record<string, number> = {};
        files.forEach((item) => {
            counts[item.photoTypeCode] = (counts[item.photoTypeCode] ?? 0) + 1;
        });
        return counts;
    }, [files]);

    const filteredFiles = useMemo(
        () => files.filter((item) => item.photoTypeCode === activePhotoTypeCode),
        [files, activePhotoTypeCode],
    );

    useEffect(() => {
        if (uploadBasePrefix) {
            void ensureRetouchS3Folder(uploadBasePrefix);
        }
    }, [uploadBasePrefix]);

    // Ekrandaki farklı barkodlar (yükleme ilerlemesi gibi diğer değişikliklerde değişmeyen sabit anahtar).
    const barcodesKey = useMemo(
        () => Array.from(new Set(files.map((item) => item.barcode).filter(Boolean))).sort().join(','),
        [files],
    );

    // Yeni eklenen barkodlar için geçmiş üretim kontrolü: debounce + tek toplu istek, barkod başına bir kez.
    useEffect(() => {
        if (!barcodesKey) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            const pending = barcodesKey
                .split(',')
                .filter((barcode) => !checkedHistoryBarcodesRef.current.has(barcode));
            if (pending.length === 0) {
                return;
            }
            pending.forEach((barcode) => checkedHistoryBarcodesRef.current.add(barcode));

            const chunks: string[][] = [];
            for (let i = 0; i < pending.length; i += HISTORY_CHECK_CHUNK_SIZE) {
                chunks.push(pending.slice(i, i + HISTORY_CHECK_CHUNK_SIZE));
            }
            chunks.forEach((chunk) => {
                void BatchProcessTrackingQuery.GetBarcodeProductionSummaries(chunk)
                    .then((summaries) => {
                        if (!summaries.some((summary) => summary.hasPreviousProduction)) {
                            return;
                        }
                        setPreviousProductions((prev) => {
                            const next = { ...prev };
                            summaries.forEach((summary) => {
                                if (summary.hasPreviousProduction) {
                                    next[summary.barcode] = summary;
                                }
                            });
                            return next;
                        });
                    })
                    .catch(() => {
                        // Başarısızsa bu barkodlar tekrar denenebilsin.
                        chunk.forEach((barcode) => checkedHistoryBarcodesRef.current.delete(barcode));
                    });
            });
        }, HISTORY_CHECK_DEBOUNCE_MS);

        return () => window.clearTimeout(timer);
    }, [barcodesKey]);

    // Uyarılar yalnızca ekranda hâlâ dosyası olan barkodlar için gösterilir (silinince kalkar).
    const visibleProductionSummaries = useMemo(() => {
        const onScreen = new Set(barcodesKey ? barcodesKey.split(',') : []);
        return Object.values(previousProductions).filter((summary) => onScreen.has(summary.barcode));
    }, [previousProductions, barcodesKey]);

    const loadPhotoTypes = useCallback(async () => {
        setPhotoTypesLoading(true);
        try {
            const response = await RawImageUploadQuery.GetPhotoTypes();
            const items = response.items ?? [];
            setPhotoTypes(items);
            if (items.length > 0) {
                setActivePhotoTypeCode(items[0].code);
            }
        } catch {
            Swal.fire(t('studio.bulkImageUpload.loadPhotoTypesError'), '', 'error');
        } finally {
            setPhotoTypesLoading(false);
        }
    }, [t]);

    const loadUploadConcurrencyLimit = useCallback(async () => {
        try {
            const capabilities = await WorkAssignmentQuery.GetContext(WORK_ASSIGNMENT_FEATURE_KEY);
            setMaxDownloadCount(
                capabilities.maxDownloadCount > 0
                    ? capabilities.maxDownloadCount
                    : DEFAULT_MAX_DOWNLOAD_COUNT,
            );
        } catch {
            setMaxDownloadCount(DEFAULT_MAX_DOWNLOAD_COUNT);
        }
    }, []);

    useEffect(() => {
        void loadPhotoTypes();
        void loadUploadConcurrencyLimit();
    }, [loadPhotoTypes, loadUploadConcurrencyLimit]);

    const hydrateInProgressUploads = useCallback(async () => {
        const username = getCurrentUsername();
        if (!username || photoTypes.length === 0) {
            return;
        }

        setIsHydrating(true);
        try {
            const response = await BatchProcessTrackingQuery.GetItems({
                pageNumber: 1,
                pageSize: 100,
                status: RAW_SHOT_STARTED,
                createdBy: normalizeCreatedBy(username),
            });

            const pendingFrames = (response.items ?? []).flatMap((item) =>
                (item.frames ?? [])
                    .filter((frame) => frame.status === RAW_SHOT_STARTED && frame.filePath && frame.rawImageId)
                    .map((frame) => ({
                        barcode: item.barcode,
                        frame,
                    })),
            );

            if (pendingFrames.length === 0) {
                return;
            }

            const keys = pendingFrames.map((entry) => entry.frame.filePath);
            const presigned = await GetS3PresignedDownloadUrls(keys);
            const urlByKey: Record<string, string> = {};
            (presigned.items ?? []).forEach((entry) => {
                if (entry.key && entry.url) {
                    urlByKey[entry.key] = entry.url;
                }
            });

            const restored: UploadedFileItem[] = pendingFrames.map((entry) => {
                const photoType =
                    photoTypes.find((type) => type.code === entry.frame.photoTypeCode)
                    ?? null;
                return {
                    id: `restored-${entry.frame.rawImageId}`,
                    fileName: fileNameFromPath(entry.frame.filePath),
                    barcode: entry.barcode,
                    filePath: entry.frame.filePath,
                    photoTypeCode: entry.frame.photoTypeCode || photoType?.code || '',
                    photoTypeDisplayName:
                        entry.frame.photoTypeDisplayName
                        || photoType?.displayName
                        || entry.frame.photoTypeCode,
                    previewUrl: urlByKey[entry.frame.filePath] || '',
                    rawImageId: entry.frame.rawImageId,
                    uploadProgress: 100,
                    status: 'done',
                };
            });

            setFiles((prev) => {
                // Kullanıcı hydrate bitmeden dosya eklediyse üzerine yazma.
                if (prev.length > 0) {
                    return prev;
                }
                return restored;
            });
        } catch {
            // Tracking erişilemezse boş başla; kullanıcı yeniden yükleyebilir.
        } finally {
            setIsHydrating(false);
        }
    }, [photoTypes]);

    useEffect(() => {
        if (photoTypesLoading || photoTypes.length === 0) {
            return;
        }
        void hydrateInProgressUploads();
    }, [photoTypesLoading, photoTypes, hydrateInProgressUploads]);

    const updateFile = (id: string, patch: Partial<UploadedFileItem>) => {
        setFiles((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    };

    const addInvalidFormatFile = (file: File, photoType: PhotoTypeItem) => {
        const uploadId = createUploadId();
        const previewUrl = URL.createObjectURL(file);

        setFiles((prev) => [
            ...prev,
            {
                id: uploadId,
                fileName: file.name,
                barcode: '',
                photoTypeCode: photoType.code,
                photoTypeDisplayName: photoType.displayName,
                previewUrl,
                uploadProgress: 0,
                status: 'invalid_format',
                errorMessage: t('studio.bulkImageUpload.invalidFileNameFormat'),
            },
        ]);
    };

    const processQueuedUpload = async (queued: QueuedUploadItem) => {
        const { uploadId, file, photoType, barcode, s3ObjectKey } = queued;
        const storedFileName = file.name;

        updateFile(uploadId, { status: 'uploading', uploadProgress: 0 });

        try {
            await UploadS3File(uploadBasePrefix, file, (progress) => {
                updateFile(uploadId, { uploadProgress: progress });
            });

            updateFile(uploadId, { status: 'registering', uploadProgress: 100 });

            const registerResult = await RawImageUploadCommand.RegisterFile({
                barcode,
                photoTypeCode: photoType.code,
                filePath: s3ObjectKey,
                fileName: storedFileName,
                warehouseCode: STUDIO_WAREHOUSE_CODE,
            });

            if (!registerResult.success || !registerResult.rawImageId) {
                updateFile(uploadId, {
                    status: 'error',
                    errorMessage: registerResult.message ?? t('studio.bulkImageUpload.registerFailed'),
                });
                return;
            }

            updateFile(uploadId, {
                status: 'done',
                rawImageId: registerResult.rawImageId,
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : t('studio.bulkImageUpload.uploadFailed');
            updateFile(uploadId, { status: 'error', errorMessage: message });
        }
    };

    const handleFilesSelected = async (selectedFiles: File[], rejectedCount = 0) => {
        if (!activePhotoType) {
            Swal.fire(t('studio.bulkImageUpload.selectPhotoTypeFirst'), '', 'warning');
            return;
        }

        if (rejectedCount > 0) {
            Swal.fire(t('studio.bulkImageUpload.invalidFileType'), `${rejectedCount}`, 'warning');
        }

        // Ekranda zaten olan barkodlar (aynı ürünün diğer kareleri) hariç, yeni barkodlar için tek toplu kontrol.
        // Engelleme kare bazlıdır: yalnızca activePhotoType.code işlemdeyse bloklanır.
        const onScreenBarcodes = new Set(filesRef.current.map((item) => item.barcode).filter(Boolean));
        const newBarcodes = Array.from(
            new Set(
                selectedFiles
                    .map((file) => parseProductFileName(file.name)?.barcode)
                    .filter((barcode): barcode is string => !!barcode && !onScreenBarcodes.has(barcode)),
            ),
        );
        const blockedBarcodesForCurrentFrame = new Set<string>();
        for (let i = 0; i < newBarcodes.length; i += HISTORY_CHECK_CHUNK_SIZE) {
            try {
                const summaries = await BatchProcessTrackingQuery.GetBarcodeProductionSummaries(
                    newBarcodes.slice(i, i + HISTORY_CHECK_CHUNK_SIZE),
                );
                summaries.forEach((summary) => {
                    const codes = summary.inProgressPhotoTypeCodes ?? [];
                    const isFrameInProgress =
                        codes.length > 0
                            ? codes.includes(activePhotoType.code)
                            : false;

                    if (isFrameInProgress) {
                        blockedBarcodesForCurrentFrame.add(summary.barcode);
                    }
                });
            } catch {
                // Kontrol başarısızsa yüklemeyi engelleme.
            }
        }

        if (blockedBarcodesForCurrentFrame.size > 0) {
            Swal.fire({
                icon: 'warning',
                title: t('studio.barcodeHistory.inProgressTitle'),
                html: `${t('studio.barcodeHistory.inProgressMessage')}<br/><br/>${Array.from(blockedBarcodesForCurrentFrame).join('<br/>')}`,
            }).catch(() => undefined);
        }

        const queuedUploads: QueuedUploadItem[] = [];
        const pendingItems: UploadedFileItem[] = [];

        for (const file of selectedFiles) {
            const parsed = parseProductFileName(file.name);
            if (!parsed) {
                addInvalidFormatFile(file, activePhotoType);
                continue;
            }

            if (blockedBarcodesForCurrentFrame.has(parsed.barcode)) {
                continue;
            }

            const uploadId = createUploadId();
            const s3ObjectKey = buildStudioS3ObjectKey(uploadBasePrefix, file.name);
            const previewUrl = URL.createObjectURL(file);

            pendingItems.push({
                id: uploadId,
                fileName: file.name,
                barcode: parsed.barcode,
                filePath: s3ObjectKey,
                photoTypeCode: activePhotoType.code,
                photoTypeDisplayName: activePhotoType.displayName,
                previewUrl,
                uploadProgress: 0,
                status: 'pending',
            });

            queuedUploads.push({
                uploadId,
                file,
                photoType: activePhotoType,
                barcode: parsed.barcode,
                s3ObjectKey,
            });
        }

        if (pendingItems.length > 0) {
            setFiles((prev) => [...prev, ...pendingItems]);
        }

        if (queuedUploads.length === 0) {
            return;
        }

        await runWithConcurrencyLimit(
            queuedUploads,
            maxDownloadCount || DEFAULT_MAX_DOWNLOAD_COUNT,
            (queued) => processQueuedUpload(queued),
        );
    };

    const handlePhotoTypeChange = async (fileId: string, newPhotoTypeCode: string) => {
        const item = files.find((entry) => entry.id === fileId);
        if (!item || !newPhotoTypeCode || item.photoTypeCode === newPhotoTypeCode || !item.barcode) {
            return;
        }

        const newType = photoTypes.find((entry) => entry.code === newPhotoTypeCode);
        if (!newType) {
            return;
        }

        const previousCode = item.photoTypeCode;
        const previousDisplayName = item.photoTypeDisplayName;

        updateFile(fileId, {
            photoTypeCode: newPhotoTypeCode,
            photoTypeDisplayName: newType.displayName,
        });

        if (!item.rawImageId || item.status !== 'done') {
            return;
        }

        try {
            const result = await RawImageUploadCommand.UpdatePhotoType({
                rawImageId: item.rawImageId,
                barcode: item.barcode,
                photoTypeCode: newPhotoTypeCode,
            });

            if (!result.success) {
                updateFile(fileId, {
                    photoTypeCode: previousCode,
                    photoTypeDisplayName: previousDisplayName,
                });
                Swal.fire(result.message ?? t('studio.bulkImageUpload.updatePhotoTypeFailed'), '', 'error');
            }
        } catch {
            updateFile(fileId, {
                photoTypeCode: previousCode,
                photoTypeDisplayName: previousDisplayName,
            });
            Swal.fire(t('studio.bulkImageUpload.updatePhotoTypeFailed'), '', 'error');
        }
    };

    const removeFileFromState = (id: string) => {
        setFiles((prev) => {
            const target = prev.find((item) => item.id === id);
            if (target?.previewUrl) {
                URL.revokeObjectURL(target.previewUrl);
            }
            return prev.filter((item) => item.id !== id);
        });
    };

    const handleRemoveFile = async (id: string) => {
        const item = files.find((entry) => entry.id === id);
        if (!item || deletingFileId) {
            return;
        }

        if (item.status === 'invalid_format') {
            removeFileFromState(id);
            return;
        }

        setDeletingFileId(id);
        try {
            if (item.rawImageId && item.barcode) {
                const result = await RawImageUploadCommand.DeleteFile({
                    rawImageId: item.rawImageId,
                    barcode: item.barcode,
                });

                if (!result.success) {
                    Swal.fire(result.message ?? t('studio.bulkImageUpload.deleteFailed'), '', 'error');
                    return;
                }
            } else if (item.filePath) {
                await DeleteS3Object({ key: item.filePath });
            }

            removeFileFromState(id);
        } catch {
            Swal.fire(t('studio.bulkImageUpload.deleteFailed'), '', 'error');
        } finally {
            setDeletingFileId(null);
        }
    };

    const clearLocalFilesOnly = useCallback(() => {
        filesRef.current.forEach((item) => {
            if (item.previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(item.previewUrl);
            }
        });
        setFiles([]);
    }, []);

    const handleClearAll = async () => {
        if (files.length === 0 || isClearing || isSubmitting) {
            return;
        }

        const confirmResult = await Swal.fire({
            icon: 'warning',
            title: t('studio.bulkImageUpload.clearAllConfirmTitle'),
            text: t('studio.bulkImageUpload.clearAllConfirmText'),
            showCancelButton: true,
            confirmButtonText: t('studio.bulkImageUpload.clearAll'),
            cancelButtonText: t('studio.bulkImageUpload.cancel'),
            confirmButtonColor: '#d32f2f',
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        setIsClearing(true);
        try {
            const snapshot = [...filesRef.current];
            const { registeredCount, failedCount } = await purgeRegisteredUploads(snapshot);

            if (failedCount > 0) {
                await Swal.fire({
                    icon: 'warning',
                    title: t('studio.bulkImageUpload.clearAllPartialFailed'),
                    text: t('studio.bulkImageUpload.clearAllPartialFailedDetail', {
                        failed: failedCount,
                        total: registeredCount,
                    }),
                });
            }

            clearLocalFilesOnly();
        } catch {
            await Swal.fire(t('studio.bulkImageUpload.deleteFailed'), '', 'error');
        } finally {
            setIsClearing(false);
        }
    };

    // Menü/çarpı ile sayfadan çıkınca veya sekme kapanınca = Tümünü Temizle (sil).
    useEffect(() => {
        const unlisten = history.listen((location) => {
            const stillOnPage =
                location.pathname === BULK_UPLOAD_PATH
                || location.pathname.startsWith(`${BULK_UPLOAD_PATH}/`);
            if (stillOnPage) {
                return;
            }

            if (skipLeaveCleanupRef.current) {
                skipLeaveCleanupRef.current = false;
                return;
            }

            const snapshot = filesRef.current;
            if (snapshot.length === 0) {
                return;
            }

            void purgeRegisteredUploads(snapshot);
        });

        const onPageHide = () => {
            if (skipLeaveCleanupRef.current) {
                return;
            }
            const snapshot = filesRef.current;
            if (snapshot.length === 0) {
                return;
            }
            purgeRegisteredUploadsKeepalive(snapshot);
        };

        window.addEventListener('pagehide', onPageHide);
        return () => {
            unlisten();
            window.removeEventListener('pagehide', onPageHide);
        };
    }, [history]);

    const completedFiles = files.filter((item) => item.status === 'done' && item.rawImageId);

    const getMissingPhotoTypesForItems = (items: UploadedFileItem[]): PhotoTypeItem[] => {
        const uploadedCodes = new Set(items.map((item) => item.photoTypeCode));
        return photoTypes.filter((item) => !uploadedCodes.has(item.code));
    };

    const handleCompleteUpload = async () => {
        if (files.some((item) => item.status === 'invalid_format')) {
            Swal.fire(t('studio.bulkImageUpload.hasInvalidFormatFiles'), '', 'warning');
            return;
        }

        if (completedFiles.length === 0) {
            Swal.fire(t('studio.bulkImageUpload.noFilesError'), '', 'error');
            return;
        }

        const filesByBarcode = completedFiles.reduce<Record<string, UploadedFileItem[]>>((acc, item) => {
            if (!acc[item.barcode]) {
                acc[item.barcode] = [];
            }
            acc[item.barcode].push(item);
            return acc;
        }, {});

        // Barkodun geçmişte çekimi/üretimi varsa (kare tipi fark etmez) göndermeden önce uyar.
        // Tek toplu istek (50+ barkod için tek çağrı); hata olursa ekrandaki önbellekle devam edilir.
        const submitBarcodes = Object.keys(filesByBarcode);
        let withHistory: BarcodeProductionSummary[];
        try {
            const summaries: BarcodeProductionSummary[] = [];
            for (let i = 0; i < submitBarcodes.length; i += HISTORY_CHECK_CHUNK_SIZE) {
                summaries.push(
                    ...(await BatchProcessTrackingQuery.GetBarcodeProductionSummaries(
                        submitBarcodes.slice(i, i + HISTORY_CHECK_CHUNK_SIZE),
                    )),
                );
            }
            withHistory = summaries.filter((summary) => summary.hasPreviousProduction);
        } catch {
            withHistory = submitBarcodes
                .map((barcode) => previousProductions[barcode])
                .filter((summary): summary is BarcodeProductionSummary => Boolean(summary));
        }
        if (withHistory.length > 0) {
            const links = withHistory
                .map((summary) => {
                    const url = PageUrl.studioRoutes.barcodeProductionHistory.url.replace(
                        ':barcode',
                        encodeURIComponent(summary.barcode),
                    );
                    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${summary.barcode} (${summary.totalProductionCount})</a>`;
                })
                .join('<br/>');
            const previousResult = await Swal.fire({
                title: t('studio.barcodeHistory.confirmPreviousTitle'),
                html: `${t('studio.barcodeHistory.confirmPreviousMessage')}<br/><br/>${links}`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('studio.barcodeHistory.confirmPreviousContinue'),
                cancelButtonText: t('studio.bulkImageUpload.confirmIncomplete.cancel'),
            });

            if (!previousResult.isConfirmed) {
                return;
            }
        }

        const missingMessages = Object.entries(filesByBarcode)
            .map(([barcode, items]) => {
                const missingTypes = getMissingPhotoTypesForItems(items);
                if (missingTypes.length === 0) {
                    return null;
                }
                const typeLabels = missingTypes
                    .map((item) => item.displayName || t(`studio.bulkImageUpload.tabs.${item.code}`, item.code))
                    .join(', ');
                return t('studio.bulkImageUpload.confirmIncomplete.missingItem', {
                    barcode,
                    types: typeLabels,
                });
            })
            .filter((message): message is string => Boolean(message));

        if (missingMessages.length > 0) {
            const confirmResult = await Swal.fire({
                title: t('studio.bulkImageUpload.confirmIncomplete.title'),
                text: t('studio.bulkImageUpload.confirmIncomplete.message', {
                    missing: missingMessages.join('; '),
                }),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: t('studio.bulkImageUpload.confirmIncomplete.confirm'),
                cancelButtonText: t('studio.bulkImageUpload.confirmIncomplete.cancel'),
            });

            if (!confirmResult.isConfirmed) {
                return;
            }
        }

        setIsSubmitting(true);
        try {
            for (const [barcode, items] of Object.entries(filesByBarcode)) {
                const result = await RawImageUploadCommand.CompleteUpload({
                    barcode,
                    rawImageIds: items.map((item) => item.rawImageId!),
                });

                if (!result.success) {
                    Swal.fire(
                        result.message ?? t('studio.bulkImageUpload.completeFailed'),
                        barcode,
                        'error',
                    );
                    return;
                }
            }

            // Complete sonrası kayıtlar silinmez; sayfa çıkışındaki silme cleanup'ını atla.
            skipLeaveCleanupRef.current = true;
            clearLocalFilesOnly();
            history.push(PageUrl.studioRoutes.rawImageFrameControl.url);
        } catch {
            Swal.fire(t('studio.bulkImageUpload.completeFailed'), '', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasInProgress = files.some(
        (item) => item.status === 'pending' || item.status === 'uploading' || item.status === 'registering',
    );
    const hasInvalidFormat = files.some((item) => item.status === 'invalid_format');
    // Complete akışı dışında drop açık kalsın; yüklemeler kuyruklu ilerler, yeni dosya eklenebilir.
    const dropDisabled = isSubmitting || isClearing || isHydrating;

    const statusMessage = (() => {
        if (hasInProgress) {
            const activeCount = files.filter(
                (item) => item.status === 'uploading' || item.status === 'registering',
            ).length;
            const pendingCount = files.filter((item) => item.status === 'pending').length;
            return t('studio.bulkImageUpload.uploadInProgressWithConcurrency', {
                active: activeCount,
                pending: pendingCount,
                limit: maxDownloadCount,
            });
        }
        if (hasInvalidFormat) {
            return t('studio.bulkImageUpload.hasInvalidFormatFiles');
        }
        return t('studio.bulkImageUpload.readyToUpload');
    })();

    return (
        <SCUIContent>
            <SCUIBreadCrumb
                title={t('studio.bulkImageUpload.title')}
                subTitle={t('studio.bulkImageUpload.subtitle')}
            />

            {(photoTypesLoading || isHydrating || isClearing) && <SCUILoadingLinear />}

            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {t('studio.bulkImageUpload.fileNamingRuleTitle')}
                </Typography>
                <Typography variant="body2">
                    {t('studio.bulkImageUpload.fileNamingRuleDescription')}
                </Typography>
            </Alert>

            {photoTypes.length > 0 && (
                <Tabs
                    value={activePhotoTypeCode || photoTypes[0].code}
                    onChange={(_, value: string) => setActivePhotoTypeCode(value)}
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', px: 1 }}
                >
                    {photoTypes.map((item) => {
                        const tabLabel = item.displayName || t(`studio.bulkImageUpload.tabs.${item.code}`, item.code);
                        const tabCount = fileCountByPhotoType[item.code] ?? 0;

                        return (
                            <Tab
                                key={item.code}
                                value={item.code}
                                label={tabCount > 0 ? `${tabLabel} (${tabCount})` : tabLabel}
                            />
                        );
                    })}
                </Tabs>
            )}

            {/* Upload panel */}
            <Box
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    border: '1px solid #e0e0e0',
                    backgroundColor: '#fff',
                    position: 'relative',
                    minHeight: { xs: 280, md: 360 },
                }}
            >
                <BulkImageDropArea
                    accept={IMAGE_ACCEPT}
                    disabled={dropDisabled}
                    title={t('studio.bulkImageUpload.dropzoneTitle')}
                    hint={t('studio.upload.dragDropInfo')}
                    activeHint={t('general.dragToUpload')}
                    onFiles={(selected, rejectedCount) => {
                        void handleFilesSelected(selected, rejectedCount);
                    }}
                />

                {files.length > 0 && (
                    <Box sx={{ mb: 2, mt: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                {t('studio.bulkImageUpload.uploadedFiles', { count: filteredFiles.length })}
                            </Typography>
                            <SCUIButton
                                type="error"
                                sizeType="default"
                                size="s"
                                text={t('studio.bulkImageUpload.clearAll')}
                                onClick={() => void handleClearAll()}
                                disabled={dropDisabled}
                                showStartIcon
                                startIcon={<DeleteOutlineIcon />}
                            />
                        </Stack>
                        {visibleProductionSummaries.length > 0 && (
                            // Çok barkod olduğunda sayfa uzamasın diye kaydırılabilir kutu.
                            <Box sx={{ maxHeight: 180, overflowY: 'auto', mb: 1 }}>
                                {visibleProductionSummaries.map((summary) => (
                                    <Alert key={summary.barcode} severity="info" sx={{ mb: 0.5, py: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography variant="body2">
                                                {t('studio.barcodeHistory.previousProductionBanner', {
                                                    barcode: summary.barcode,
                                                    count: summary.totalProductionCount,
                                                })}
                                            </Typography>
                                            <Link
                                                href={PageUrl.studioRoutes.barcodeProductionHistory.url.replace(
                                                    ':barcode',
                                                    encodeURIComponent(summary.barcode),
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="body2"
                                                fontWeight={700}
                                            >
                                                {t('studio.barcodeHistory.viewProductions')} ↗
                                            </Link>
                                        </Stack>
                                    </Alert>
                                ))}
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(auto-fill, minmax(140px, 1fr))',
                                    sm: 'repeat(auto-fill, minmax(160px, 200px))',
                                },
                                gap: 2,
                                justifyContent: 'start',
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                border: 1,
                                borderColor: 'divider',
                            }}
                        >
                            {filteredFiles.length === 0 && (
                                <Box sx={{ gridColumn: '1 / -1', py: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('studio.bulkImageUpload.noFilesForTab')}
                                    </Typography>
                                </Box>
                            )}
                            {filteredFiles.map((item) => (
                                <Paper key={item.id} variant="outlined" sx={{ p: 1, position: 'relative' }}>
                                    <SCUIZoomableImage
                                        src={item.previewUrl}
                                        alt={item.fileName}
                                        containerSx={{
                                            width: '100%',
                                            height: 140,
                                            borderRadius: 1,
                                            bgcolor: 'grey.100',
                                            border: 1,
                                            borderColor: 'divider',
                                            mb: 0.5,
                                        }}
                                        imageSx={{
                                            objectFit: 'contain',
                                            p: 0.5,
                                            borderRadius: 1,
                                        }}
                                    />
                                    <Typography variant="caption" noWrap display="block" title={item.fileName}>
                                        {item.fileName}
                                    </Typography>
                                    {item.barcode && (
                                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                            {item.barcode}
                                        </Typography>
                                    )}
                                    {item.status === 'invalid_format' ? (
                                        <Chip
                                            label={t('studio.bulkImageUpload.invalidFileNameFormat')}
                                            color="error"
                                            size="small"
                                            sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
                                        />
                                    ) : (
                                        <Box sx={{ mt: 0.5 }}>
                                            <SCUISelectBox
                                                label={t('studio.bulkImageUpload.photoTypeLabel')}
                                                showHeadLabel={false}
                                                size="s"
                                                value={item.photoTypeCode}
                                                list={photoTypeSelectList}
                                                selectList={[
                                                    {
                                                        id: item.photoTypeCode,
                                                        value: item.photoTypeDisplayName,
                                                    },
                                                ]}
                                                stateName={`photoType-${item.id}`}
                                                disabled={
                                                    item.status === 'pending'
                                                    || item.status === 'uploading'
                                                    || item.status === 'registering'
                                                    || isSubmitting
                                                }
                                                onChange={(selected) => {
                                                    const nextCode = selected?.[0]?.id;
                                                    if (typeof nextCode === 'string') {
                                                        void handlePhotoTypeChange(item.id, nextCode);
                                                    }
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {item.status === 'pending' && (
                                        <Chip
                                            label={t('studio.bulkImageUpload.statusPending')}
                                            size="small"
                                            sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
                                        />
                                    )}
                                    {(item.status === 'uploading' || item.status === 'registering') && (
                                        <Chip
                                            label={
                                                item.status === 'registering'
                                                    ? t('studio.bulkImageUpload.statusRegistering')
                                                    : t('studio.bulkImageUpload.statusUploading')
                                            }
                                            color="info"
                                            size="small"
                                            sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
                                        />
                                    )}
                                    {item.status === 'done' && (
                                        <Chip
                                            label={t('studio.bulkImageUpload.statusDone')}
                                            color="success"
                                            size="small"
                                            sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
                                        />
                                    )}
                                    {item.status !== 'done'
                                        && item.status !== 'error'
                                        && item.status !== 'invalid_format'
                                        && item.status !== 'pending' && (
                                        <LinearProgress
                                            variant="determinate"
                                            value={item.uploadProgress}
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                    {item.status === 'pending' && (
                                        <LinearProgress
                                            variant="indeterminate"
                                            sx={{ mt: 1, opacity: 0.35 }}
                                        />
                                    )}
                                    {item.status === 'error' && (
                                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                            {item.errorMessage}
                                        </Typography>
                                    )}
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={
                                            item.status === 'pending'
                                            || item.status === 'uploading'
                                            || item.status === 'registering'
                                            || deletingFileId === item.id
                                        }
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            bgcolor: 'background.paper',
                                            boxShadow: 1,
                                            '&:hover': { bgcolor: 'background.paper' },
                                        }}
                                        onClick={() => void handleRemoveFile(item.id)}
                                        aria-label={t('general.delete')}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                )}

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    pt={2}
                    borderTop={1}
                    borderColor="divider"
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                            {statusMessage}
                        </Typography>
                    </Stack>
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('studio.bulkImageUpload.submit')}
                        onClick={handleCompleteUpload}
                        disabled={
                            isSubmitting
                            || hasInProgress
                            || hasInvalidFormat
                            || completedFiles.length === 0
                        }
                        showStartIcon
                        startIcon={<CloudUploadOutlinedIcon />}
                    />
                </Stack>
            </Box>

        </SCUIContent>
    );
};
